import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  CalendarClock,
  Eye,
  History,
  School as SchoolIcon,
  UserPlus,
} from 'lucide-react';
import { migrationV1V2Api } from './api/migration-v1-v2';
import type {
  AutoCatchUpSchoolSummary,
  AutoCatchUpStats,
  AutoCatchUpSweep,
} from './types';
import {
  formatDateTime,
  formatDuration,
  relativeTime,
  scopeLabel,
  triggerLabel,
} from './components/autoCatchUpPresentation';
import { SweepStatusBadge } from './components/AutoCatchUpPills';

/**
 * Journal du rattrapage automatique des utilisateurs, sous ses deux angles :
 *
 *  - **Exécutions** : chaque passe de la tâche programmée, y compris les nuits
 *    où il n'y avait rien à reprendre — c'est ce qui prouve qu'elle tourne ;
 *  - **Par école** : ce qui a été repris sur chaque dossier depuis sa bascule.
 *
 * Les deux mènent au même détail nominatif, par des chemins différents.
 */

function StatCard({
  title,
  value,
  hint,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm">
      <div className="p-4 flex items-center gap-3">
        <div className={cn('p-2.5 rounded-lg', color)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          {hint && <p className="text-xs text-gray-400 truncate">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

export function AutoCatchUpPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'schools' ? 'schools' : 'sweeps';

  const [stats, setStats] = useState<AutoCatchUpStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  // --- Exécutions ---
  const [sweeps, setSweeps] = useState<AutoCatchUpSweep[]>([]);
  const [sweepsLoading, setSweepsLoading] = useState(true);
  const [sweepPage, setSweepPage] = useState(1);
  const [sweepPageSize, setSweepPageSize] = useState(25);
  const [sweepTotal, setSweepTotal] = useState(0);
  const [sweepPages, setSweepPages] = useState(1);
  const [trigger, setTrigger] = useState('all');
  const [onlyWithAdditions, setOnlyWithAdditions] = useState(false);
  const [includeDryRun, setIncludeDryRun] = useState(false);

  // --- Par école ---
  const [schools, setSchools] = useState<AutoCatchUpSchoolSummary[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(true);
  const [schoolPage, setSchoolPage] = useState(1);
  const [schoolPageSize, setSchoolPageSize] = useState(25);
  const [schoolTotal, setSchoolTotal] = useState(0);
  const [schoolPages, setSchoolPages] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'last' | 'added' | 'school'>('last');

  const loadSweeps = useCallback(async () => {
    setSweepsLoading(true);
    try {
      const res = await migrationV1V2Api.getAutoCatchUpSweeps({
        page: sweepPage,
        perPage: sweepPageSize,
        trigger: trigger === 'all' ? undefined : trigger,
        onlyWithAdditions: onlyWithAdditions || undefined,
        includeDryRun: includeDryRun || undefined,
      });
      setSweeps(res.data);
      setSweepTotal(res.pagination.total);
      setSweepPages(Math.max(1, res.pagination.pages));
      setStats(res.stats);
      setError(null);
    } catch {
      setError("Impossible de charger l'historique des passes de rattrapage.");
    } finally {
      setSweepsLoading(false);
    }
  }, [sweepPage, sweepPageSize, trigger, onlyWithAdditions, includeDryRun]);

  const loadSchools = useCallback(async () => {
    setSchoolsLoading(true);
    try {
      const res = await migrationV1V2Api.getAutoCatchUpSchools({
        page: schoolPage,
        perPage: schoolPageSize,
        search: search || undefined,
        sort,
      });
      setSchools(res.data);
      setSchoolTotal(res.pagination.total);
      setSchoolPages(Math.max(1, res.pagination.pages));
    } catch {
      setError('Impossible de charger le rattrapage par école.');
    } finally {
      setSchoolsLoading(false);
    }
  }, [schoolPage, schoolPageSize, search, sort]);

  useEffect(() => {
    void loadSweeps();
  }, [loadSweeps]);

  useEffect(() => {
    const timer = setTimeout(() => void loadSchools(), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadSchools, search]);

  const sweepColumns: Column<AutoCatchUpSweep>[] = [
    {
      key: 'startedAt',
      header: 'Lancée le',
      className: 'w-48',
      render: (sweep) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {formatDateTime(sweep.startedAt)}
          </div>
          <div className="text-xs text-gray-400">{relativeTime(sweep.startedAt)}</div>
        </div>
      ),
    },
    {
      key: 'trigger',
      header: 'Déclencheur',
      className: 'w-52',
      render: (sweep) => (
        <div className="text-sm">
          <div className="text-gray-700 dark:text-gray-200">{triggerLabel(sweep.trigger)}</div>
          <div className="text-xs text-gray-400">
            {scopeLabel(sweep)}
            {sweep.triggeredBy && ` · ${sweep.triggeredBy.firstname} ${sweep.triggeredBy.lastname}`}
            {sweep.dryRun && ' · simulation'}
          </div>
        </div>
      ),
    },
    {
      key: 'scanned',
      header: 'Écoles balayées',
      className: 'w-32 text-right',
      render: (sweep) => (
        <span className="tabular-nums text-sm">
          {sweep.projectsScanned}
          {sweep.projectsEligible > sweep.projectsScanned && (
            <span className="text-gray-400"> / {sweep.projectsEligible}</span>
          )}
        </span>
      ),
    },
    {
      key: 'added',
      header: 'Éléments migrés',
      className: 'w-36 text-right',
      render: (sweep) =>
        sweep.addedRecords > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-sky-100 px-2 py-0.5 text-sm font-semibold tabular-nums text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
            <UserPlus className="h-3 w-3" />
            {sweep.addedRecords}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'schoolsServed',
      header: 'Écoles servies',
      className: 'w-32 text-right',
      render: (sweep) => (
        <span className="tabular-nums text-sm text-gray-600 dark:text-gray-300">
          {sweep.projectsWithAdditions || '—'}
        </span>
      ),
    },
    {
      key: 'errors',
      header: 'Incidents',
      className: 'w-28 text-right',
      render: (sweep) =>
        sweep.projectsInError > 0 || sweep.failedRecords > 0 ? (
          <span className="text-sm font-medium text-red-600 dark:text-red-400 tabular-nums">
            {sweep.projectsInError > 0 && `${sweep.projectsInError} école${sweep.projectsInError > 1 ? 's' : ''}`}
            {sweep.projectsInError > 0 && sweep.failedRecords > 0 && ' · '}
            {sweep.failedRecords > 0 && `${sweep.failedRecords} fiche${sweep.failedRecords > 1 ? 's' : ''}`}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'duration',
      header: 'Durée',
      className: 'w-24 text-right',
      render: (sweep) => (
        <span className="text-sm text-gray-500 tabular-nums">
          {formatDuration(sweep.durationSeconds)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      className: 'w-44',
      render: (sweep) => <SweepStatusBadge sweep={sweep} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right w-28',
      render: (sweep) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/migration-v1-v2/rattrapage/${sweep.id}`)}
        >
          <Eye className="h-4 w-4 mr-1" />
          Détail
        </Button>
      ),
    },
  ];

  const schoolColumns: Column<AutoCatchUpSchoolSummary>[] = [
    {
      key: 'school',
      header: 'École',
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">{row.name}</span>
          <Badge variant="outline" className="font-mono text-xs">
            {row.code}
          </Badge>
        </div>
      ),
    },
    {
      key: 'added',
      header: 'Éléments migrés',
      className: 'w-36 text-right',
      render: (row) => (
        <span className="inline-flex items-center gap-1 rounded-md bg-sky-100 px-2 py-0.5 text-sm font-semibold tabular-nums text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
          <UserPlus className="h-3 w-3" />
          {row.added}
        </span>
      ),
    },
    {
      key: 'failed',
      header: 'Échecs',
      className: 'w-24 text-right',
      render: (row) =>
        row.failed > 0 ? (
          <span className="text-sm font-medium text-red-600 dark:text-red-400 tabular-nums">
            {row.failed}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'runs',
      header: 'Passes avec ajout',
      className: 'w-36 text-right',
      render: (row) => (
        <span className="text-sm text-gray-600 dark:text-gray-300 tabular-nums">{row.runs}</span>
      ),
    },
    {
      key: 'lastRunAt',
      header: 'Dernier ajout',
      className: 'w-48',
      render: (row) => (
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-200">
            {formatDateTime(row.lastRunAt)}
          </div>
          <div className="text-xs text-gray-400">{relativeTime(row.lastRunAt)}</div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right w-52',
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/migration-v1-v2/rattrapage/ecoles/${row.projectId}`)}
          >
            <History className="h-4 w-4 mr-1" />
            Historique
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/migration-v1-v2/${row.projectId}`)}
          >
            <Eye className="h-4 w-4 mr-1" />
            Dossier
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-white">
            <UserPlus className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Rattrapage automatique
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
              Chaque nuit, les personnes inscrites en V1 depuis la bascule sont créées en V2 sur
              les dossiers déjà migrés, puis rattachées à leur classe et à leurs groupes. Aucune
              donnée déjà migrée n'est modifiée.
            </p>
          </div>
        </div>
        <Link to="/migration-v1-v2/board">
          <Button variant="outline" size="sm">
            Board des migrations
          </Button>
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Éléments migrés"
          value={stats?.totalAdded ?? 0}
          hint="depuis la mise en service"
          icon={UserPlus}
          color="bg-sky-100 text-sky-600"
        />
        <StatCard
          title="Sur 30 jours"
          value={stats?.added30d ?? 0}
          hint={`${stats?.sweeps30d ?? 0} passe${(stats?.sweeps30d ?? 0) > 1 ? 's' : ''}`}
          icon={CalendarClock}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          title="Dernière passe"
          value={stats?.lastSweepAt ? relativeTime(stats.lastSweepAt) : '—'}
          hint={stats?.lastSweepAt ? formatDateTime(stats.lastSweepAt) : 'jamais exécutée'}
          icon={History}
          color="bg-violet-100 text-violet-600"
        />
        <StatCard
          title="Fiches en échec"
          value={stats?.totalFailed ?? 0}
          hint="à traiter à la main"
          icon={AlertTriangle}
          color={
            (stats?.totalFailed ?? 0) > 0
              ? 'bg-red-100 text-red-600'
              : 'bg-gray-100 text-gray-500'
          }
        />
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
      )}

      <Tabs
        value={tab}
        onValueChange={(value) =>
          setSearchParams(value === 'schools' ? { tab: 'schools' } : {}, { replace: true })
        }
      >
        <TabsList>
          <TabsTrigger value="sweeps">Exécutions</TabsTrigger>
          <TabsTrigger value="schools">Par école</TabsTrigger>
        </TabsList>

        <TabsContent value="sweeps" className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <Select
              value={trigger}
              onValueChange={(value) => {
                setTrigger(value);
                setSweepPage(1);
              }}
            >
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les déclencheurs</SelectItem>
                <SelectItem value="cron">Tâche programmée</SelectItem>
                <SelectItem value="manual">Depuis l'interface</SelectItem>
                <SelectItem value="cli">Ligne de commande</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Switch
                id="only-with-additions"
                checked={onlyWithAdditions}
                onCheckedChange={(checked) => {
                  setOnlyWithAdditions(checked);
                  setSweepPage(1);
                }}
              />
              <Label htmlFor="only-with-additions" className="text-sm font-normal">
                Uniquement les passes ayant migré
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="include-dry-run"
                checked={includeDryRun}
                onCheckedChange={(checked) => {
                  setIncludeDryRun(checked);
                  setSweepPage(1);
                }}
              />
              <Label htmlFor="include-dry-run" className="text-sm font-normal">
                Inclure les simulations
              </Label>
            </div>
          </div>

          <DataTable
            columns={sweepColumns}
            data={sweeps}
            loading={sweepsLoading}
            page={sweepPage}
            pageSize={sweepPageSize}
            totalItems={sweepTotal}
            totalPages={sweepPages}
            onPageChange={setSweepPage}
            onPageSizeChange={(size) => {
              setSweepPageSize(size);
              setSweepPage(1);
            }}
            emptyMessage="Aucune passe enregistrée — la tâche programmée n'a pas encore tourné."
          />
        </TabsContent>

        <TabsContent value="schools" className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <Select
              value={sort}
              onValueChange={(value) => {
                setSort(value as 'last' | 'added' | 'school');
                setSchoolPage(1);
              }}
            >
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last">Dernier ajout d'abord</SelectItem>
                <SelectItem value="added">Plus d'éléments migrés</SelectItem>
                <SelectItem value="school">Nom d'école</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
              <SchoolIcon className="h-4 w-4" />
              Seules les écoles ayant reçu quelque chose apparaissent ici.
            </p>
          </div>

          <DataTable
            columns={schoolColumns}
            data={schools}
            loading={schoolsLoading}
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setSchoolPage(1);
            }}
            searchPlaceholder="Rechercher une école par nom ou code…"
            page={schoolPage}
            pageSize={schoolPageSize}
            totalItems={schoolTotal}
            totalPages={schoolPages}
            onPageChange={setSchoolPage}
            onPageSizeChange={(size) => {
              setSchoolPageSize(size);
              setSchoolPage(1);
            }}
            keyExtractor={(row) => row.projectId}
            emptyMessage="Aucune école n'a encore reçu d'ajout automatique."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
